import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Input — upgraded in Phase 12 for WCAG 2.2 compliance.
 *
 * Backward-compatible: the className surface and default height are
 * unchanged, so every existing caller renders identically.
 *
 * New props (opt-in):
 *   - `error` — string|boolean. When truthy, sets aria-invalid and
 *     renders the error message with role="alert" linked via
 *     aria-describedby. (WCAG 3.3.1, 4.1.3)
 *   - `describedBy` / `ariaDescribedBy` — forward additional description
 *     IDs in addition to the auto-linked error/description.
 *   - `description` — supporting text rendered below the input and
 *     associated via aria-describedby.
 *   - `redundantEntry` — when true, adds autoComplete hints so browsers
 *     / password managers can fill from a previous entry. (WCAG 3.3.7)
 *   - `autoCompleteHint` — explicit autocomplete token when the field
 *     participates in Redundant Entry (e.g. 'email', 'given-name').
 *
 * Focus ring bumped from ring-1 → ring-2 to satisfy WCAG 2.4.13.
 */
const Input = React.forwardRef(
  (
    {
      className,
      type,
      id: providedId,
      error,
      description,
      describedBy,
      ariaDescribedBy,
      redundantEntry = false,
      autoCompleteHint,
      autoComplete,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const id = providedId || `ds-input-${generatedId}`;
    const errorId = error ? `${id}-error` : undefined;
    const descriptionId = description ? `${id}-desc` : undefined;

    const describedByIds = [
      descriptionId,
      errorId,
      describedBy,
      ariaDescribedBy,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

    const resolvedAutoComplete =
      autoComplete || (redundantEntry ? autoCompleteHint || "on" : undefined);

    const hasError = Boolean(error);

    return (
      <>
        <input
          id={id}
          type={type}
          aria-invalid={hasError ? "true" : undefined}
          aria-describedby={describedByIds}
          autoComplete={resolvedAutoComplete}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            hasError &&
              "border-destructive focus-visible:ring-destructive aria-[invalid=true]:border-destructive",
            className
          )}
          ref={ref}
          {...props}
        />
        {description && (
          <p id={descriptionId} className="mt-1 text-xs text-muted-foreground">
            {description}
          </p>
        )}
        {hasError && typeof error === "string" && (
          <p
            id={errorId}
            role="alert"
            className="mt-1 text-xs text-destructive"
          >
            {error}
          </p>
        )}
      </>
    );
  }
);
Input.displayName = "Input"

export { Input }
